import type { WelcomePrompt, WelcomePromptCategory } from '../types';

/**
 * Generic prompt categories used when no contextual prompts (derived from the
 * user's MRs and issues) apply. The CLI resolves these into
 * `AppState.welcomePrompts` as the fallback, and uses them to pad contextual
 * categories that have fewer prompts than the card displays.
 *
 * `satisfies` (rather than a type annotation) keeps the inferred `string[]`
 * example type, so consumers that only deal with generic prompts don't have
 * to handle segmented ones.
 */
export const DEFAULT_WELCOME_CATEGORIES = [
  {
    label: 'Your MRs',
    examples: [
      'List my open merge requests',
      'Summarize the review comments on my merge requests',
      'Draft a merge request description for my current branch',
      "What's left to do on my merge requests?",
    ],
  },
  {
    label: 'Issues assigned to you',
    examples: [
      'List the issues assigned to me',
      'Summarize the issues assigned to me',
      'Suggest which issue I should pick up next',
      'Draft an implementation plan for one of my issues',
    ],
  },
] satisfies WelcomePromptCategory[];

/** Full text of a prompt as displayed in the welcome card. */
export const welcomePromptDisplayText = (prompt: WelcomePrompt): string =>
  typeof prompt === 'string' ? prompt : prompt.map((segment) => segment.text).join('');

/**
 * Text submitted to the agent when the prompt is run. Title segments are
 * display-only context for the user — the work item reference is enough for
 * the agent — so they are omitted here.
 */
export const welcomePromptRunText = (prompt: WelcomePrompt): string =>
  typeof prompt === 'string'
    ? prompt
    : prompt
        .filter((segment) => segment.kind !== 'title')
        .map((segment) => segment.text)
        .join('');
